import {
  Button,
  CleanUpIcon,
  Divider,
  makeStyles,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  useTranslation
} from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useClientOptions } from '@paintscout/ui-business'
import { getDetails, getQuoteOptions } from '@paintscout/util/builder'
import VisibilityIcon from '@material-ui/icons/VisibilityOutlined'
import VisibilityOffIcon from '@material-ui/icons/VisibilityOffOutlined'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import TuneIcon from '@material-ui/icons/TuneOutlined'
import classnames from 'classnames'
import React, { useState } from 'react'
import { useQuote } from '../context'

/** Which card sections the view renders. 'all' shows both. */
export type WorkOrderSectionFilter = 'all' | 'area' | 'substrate'

export interface WorkOrderNavSection {
  key: WorkOrderSectionFilter
  label: string
  Icon?: React.ComponentType<{ className?: string }>
}

export interface WorkOrderNavBarNextProps {
  /** The filter pills to offer, in order (the view builds these from what has content). */
  sections: WorkOrderNavSection[]
  /** Currently-selected filter. */
  value: WorkOrderSectionFilter
  onChange: (value: WorkOrderSectionFilter) => void
  hiddenCount: number
  /** Whether the Hidden items section is expanded (the view renders it above the cards). */
  hiddenOpen: boolean
  onToggleHidden: () => void
  /**
   * Clean-up handlers keyed by section key ('area' | 'substrate'); a key is present only when that
   * view has customizations. The bar shows a single Clean up for whatever is currently showing.
   */
  cleanUp?: Partial<Record<string, () => void>>
  /** Global hour-visibility handler (mirrors WorkOrderView.handleToggleAmount). */
  onToggleAmount: (dropdownValue: string) => void
  /** Toggles the product lines on every card (quote option `showWorkOrderProducts`). */
  onToggleProducts: () => void
}

const useStyles = makeStyles(
  (theme: Theme) => {
    const neutral = theme.paintscout.palette.brand.Neutral
    return {
      root: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(1),
        '@media print': { display: 'none' }
      },
      pills: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
        minWidth: 0
      },
      pill: {
        textTransform: 'none',
        fontWeight: theme.typography.fontWeightRegular as number,
        fontSize: theme.typography.pxToRem(14),
        color: neutral.P50,
        border: `1px solid ${neutral.P85}`,
        // Fully round, matching the mock's 26px-radius filter pills.
        borderRadius: theme.borderRadius?.full ?? '9999px',
        backgroundColor: neutral.White,
        minHeight: 28,
        padding: theme.spacing(0, 1.5),
        '& .MuiButton-startIcon': { marginRight: theme.spacing(0.5) },
        '& svg': { fontSize: 16 },
        '&:hover': { backgroundColor: neutral.P98, borderColor: neutral.P85 }
      },
      // Selected filter: filled, darker label, same outline.
      pillActive: {
        backgroundColor: neutral.P96,
        color: neutral.P30,
        '&:hover': { backgroundColor: neutral.P94, borderColor: neutral.P85 }
      },
      right: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flexShrink: 0
      },
      // Compact action chips (the prototype's 28px / 12px-label row): quiet until hovered, with a
      // pressed fill while their state is applied.
      actionButton: {
        textTransform: 'none',
        color: neutral.P50,
        fontWeight: 500,
        fontSize: theme.typography.pxToRem(13),
        minHeight: 28,
        padding: theme.spacing(0, 1),
        // `lg` (8px) — closest token to the mock's 6px action chips. (`sm` is 2px, not 6.)
        borderRadius: theme.borderRadius?.lg ?? 8,
        '& .MuiButton-startIcon': { marginRight: theme.spacing(0.5) },
        '& svg': { fontSize: 16 },
        '&:hover': { backgroundColor: neutral.P96 }
      },
      // Pressed state for a toggle action that is currently applied (products hidden, section open).
      actionButtonActive: {
        backgroundColor: neutral.P96,
        color: neutral.P40,
        '&:hover': { backgroundColor: neutral.P94 }
      },
      actionDivider: {
        width: 1,
        height: 11,
        borderRadius: theme.borderRadius.full,
        backgroundColor: neutral.P85
      },
      chevron: {
        fontSize: 18,
        marginLeft: theme.spacing(0.5),
        color: neutral.P60
      }
    }
  },
  { name: 'WorkOrderNavBarNext' }
)

/** Hour-visibility dropdown options (mirrors the old per-section control). */
function getDefaultToggleOption(quoteOptions: ReturnType<typeof getQuoteOptions>): string {
  const itemKeys = [
    'showWorkOrderAreaValues',
    'showWorkOrderSubstrateValues',
    'showWorkOrderAreaOptionValues',
    'showWorkOrderSubstrateOptionValues'
  ]
  const allTrue = itemKeys.every((key) => quoteOptions[key] === true)
  const allFalse = itemKeys.every((key) => quoteOptions[key] === false)
  if (allTrue) {
    return quoteOptions.showWorkOrderTotalHours === false ? 'hideTotalHours' : 'showAllHours'
  }
  if (allFalse) {
    return quoteOptions.showWorkOrderTotalHours === true ? 'hideItemHours' : 'hideAllHours'
  }
  return 'showAllHours'
}

/**
 * The Work Order top navigation bar. Left: All / Areas / Surfaces pills that filter which card
 * sections the view renders (collapsing to a single dropdown on mobile). Right: "Hidden items (N)"
 * (expands the section the view renders above the cards), Clean up for whatever is showing, a "Hide
 * all products" toggle, and the global hours dropdown.
 */
export default function WorkOrderNavBarNext({
  sections,
  value,
  onChange,
  hiddenCount,
  hiddenOpen,
  onToggleHidden,
  cleanUp,
  onToggleAmount,
  onToggleProducts
}: WorkOrderNavBarNextProps) {
  const classes = useStyles()
  const theme = useTheme()
  // Collapse the left pills into a dropdown once the row gets tight (keeps the right-side actions —
  // Clean up / Hidden items / Show all hours — together on one line).
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { __ } = useTranslation()
  const { quote } = useQuote()
  const { options } = useClientOptions()

  const [navAnchor, setNavAnchor] = useState<HTMLElement | null>(null)
  const [hoursAnchor, setHoursAnchor] = useState<HTMLElement | null>(null)
  // Mobile: every right-side action collapses behind one "View" chip (see viewMenu).
  const [viewAnchor, setViewAnchor] = useState<HTMLElement | null>(null)

  const quoteOptions = getQuoteOptions({ options, quote })
  const productsHidden = quoteOptions.showWorkOrderProducts === false
  const details = getDetails({ quote, options })
  const allowShowHours = !details.totals.hours.useCustom || quoteOptions.allowShowCustomizedTotalHours
  const selectedToggle = getDefaultToggleOption(quoteOptions)
  const hourOptions = [
    { value: 'showAllHours', label: __('Show all hours'), icon: VisibilityIcon },
    { value: 'hideItemHours', label: __('Hide item hours'), icon: VisibilityOffIcon },
    { value: 'hideAllHours', label: __('Hide all hours'), icon: VisibilityOffIcon }
  ]
  const selectedHourOption = hourOptions.find((option) => option.value === selectedToggle) ?? hourOptions[0]

  const hoursDropdown = (
    <>
      <span className={classes.actionDivider} />
      {allowShowHours ? (
        <Button
          variant="text"
          size="small"
          className={classes.actionButton}
          startIcon={<VisibilityIcon />}
          endIcon={hoursAnchor ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          onClick={(ev: React.MouseEvent<HTMLElement>) => setHoursAnchor(ev.currentTarget)}
        >
          {selectedHourOption.label}
        </Button>
      ) : (
        <Tooltip
          content={__('Hours cannot be shown because Total Hours have been customized')}
          placement="bottom"
          hideIcon
        >
          <span>
            <Button
              variant="text"
              size="small"
              className={classes.actionButton}
              startIcon={<VisibilityIcon />}
              endIcon={<ExpandMoreIcon />}
              disabled
            >
              {selectedHourOption.label}
            </Button>
          </span>
        </Tooltip>
      )}
      <Menu
        anchorEl={hoursAnchor}
        open={Boolean(hoursAnchor)}
        onClose={() => setHoursAnchor(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {hourOptions.map((option) => (
          <MenuItem
            key={option.value}
            icon={option.icon}
            selected={option.value === selectedToggle}
            onClick={() => {
              onToggleAmount(option.value)
              setHoursAnchor(null)
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  )

  const activeSection = sections.find((section) => section.key === value) ?? sections[0]
  // A single Clean up for what's on screen: the selected view, or — while showing everything —
  // whichever view has something to clean up (area first).
  const activeCleanUp = value === 'all' ? cleanUp?.area ?? cleanUp?.substrate : cleanUp?.[value]

  // Mobile: one "View" chip in place of the Hidden items / Clean up / products / hours chips — four
  // targets don't fit beside the section filter on a phone. Same handlers, same enable/disable rules;
  // the hour options keep their selected state and go disabled (with the reason) when Total Hours has
  // been customized, matching the desktop dropdown's tooltip.
  const viewMenu = (
    <>
      <Button
        variant="text"
        size="small"
        className={classes.actionButton}
        startIcon={<TuneIcon />}
        endIcon={viewAnchor ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        onClick={(ev: React.MouseEvent<HTMLElement>) => setViewAnchor(ev.currentTarget)}
      >
        {__('View')}
      </Button>
      <Menu
        anchorEl={viewAnchor}
        open={Boolean(viewAnchor)}
        onClose={() => setViewAnchor(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {hiddenCount > 0 && (
          <MenuItem
            icon={VisibilityOffIcon}
            selected={hiddenOpen}
            onClick={() => {
              setViewAnchor(null)
              onToggleHidden()
            }}
          >
            {`${__('Hidden items')} (${hiddenCount})`}
          </MenuItem>
        )}
        {!!activeCleanUp && (
          <MenuItem
            icon={CleanUpIcon}
            onClick={() => {
              setViewAnchor(null)
              activeCleanUp()
            }}
          >
            {__('Clean up')}
          </MenuItem>
        )}
        <MenuItem
          icon={productsHidden ? VisibilityIcon : VisibilityOffIcon}
          selected={productsHidden}
          onClick={() => {
            setViewAnchor(null)
            onToggleProducts()
          }}
        >
          {productsHidden ? __('Show all products') : __('Hide all products')}
        </MenuItem>
        <Divider />
        {hourOptions.map((option) => (
          <MenuItem
            key={option.value}
            icon={option.icon}
            selected={option.value === selectedToggle}
            disabled={!allowShowHours}
            title={allowShowHours ? undefined : __('Hours cannot be shown because Total Hours have been customized')}
            onClick={() => {
              setViewAnchor(null)
              onToggleAmount(option.value)
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  )

  return (
    <div className={classes.root}>
      {/* Left: which sections to show */}
      {isMobile ? (
        <div className={classes.pills}>
          <Button
            variant="text"
            size="small"
            className={classes.pill}
            icon={activeSection?.Icon}
            onClick={(ev: React.MouseEvent<HTMLElement>) => setNavAnchor(ev.currentTarget)}
          >
            {activeSection?.label}
            <ExpandMoreIcon className={classes.chevron} />
          </Button>
          <Menu
            anchorEl={navAnchor}
            open={Boolean(navAnchor)}
            onClose={() => setNavAnchor(null)}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            {sections.map((section) => (
              <MenuItem
                key={section.key}
                icon={section.Icon}
                selected={section.key === value}
                onClick={() => {
                  onChange(section.key)
                  setNavAnchor(null)
                }}
              >
                {section.label}
              </MenuItem>
            ))}
          </Menu>
        </div>
      ) : (
        <div className={classes.pills}>
          {sections.map((section) => (
            <Button
              key={section.key}
              variant="text"
              size="small"
              icon={section.Icon}
              className={classnames(classes.pill, { [classes.pillActive]: section.key === value })}
              onClick={() => onChange(section.key)}
            >
              {section.label}
            </Button>
          ))}
        </div>
      )}

      {/* Right: Hidden items + Clean up + Hide all products + global hours — always one line on
          desktop, all of it behind the "View" chip on mobile. */}
      <div className={classes.right}>
        {isMobile && viewMenu}
        {!isMobile && hiddenCount > 0 && (
          <Button
            variant="text"
            size="small"
            icon={VisibilityOffIcon}
            className={classnames(classes.actionButton, { [classes.actionButtonActive]: hiddenOpen })}
            onClick={onToggleHidden}
          >
            <Typography component="span" variant="inherit">{`${__('Hidden items')} (${hiddenCount})`}</Typography>
          </Button>
        )}
        {!isMobile && activeCleanUp && (
          <Button
            variant="text"
            size="small"
            icon={CleanUpIcon}
            className={classes.actionButton}
            onClick={activeCleanUp}
          >
            {__('Clean up')}
          </Button>
        )}
        {/* Product lines on every card. The label names what the click does; the button also takes a
            pressed look while products are hidden (the mock's active-chip state). The divider only
            earns its place when something sits to its left. */}
        {!isMobile && (hiddenCount > 0 || activeCleanUp) && <span className={classes.actionDivider} />}
        {!isMobile && (
          <Button
            variant="text"
            size="small"
            icon={productsHidden ? VisibilityIcon : VisibilityOffIcon}
            className={classnames(classes.actionButton, { [classes.actionButtonActive]: productsHidden })}
            onClick={onToggleProducts}
          >
            {productsHidden ? __('Show all products') : __('Hide all products')}
          </Button>
        )}
        {!isMobile && hoursDropdown}
      </div>
    </div>
  )
}
